/**
 * Vimshottari Dasha — the classical 120-year planetary period cycle used
 * throughout Jyotish for life-phase timing (see docs/reference-calculator.md
 * section 4.6: Mahadasha/Antardasha/Pratyantardasha, verified against the
 * commercial reference calculator's output).
 *
 * Standard algorithm:
 * 1. The Moon's Nakshatra (27 lunar mansions, each ruled by one of the 9
 *    Vimshottari planets in a fixed repeating order) determines which
 *    planet's Mahadasha is running *at birth*.
 * 2. The *fraction already elapsed* within that Nakshatra (Moon's exact
 *    degree position, not just which Nakshatra it's in) determines how much
 *    of that first Mahadasha had already elapsed before birth — the
 *    "balance of dasha" is the remaining fraction, applied proportionally
 *    to that planet's full period length.
 * 3. All following Mahadashas run their full period length, cycling through
 *    the 9 planets in the fixed order below, indefinitely.
 *
 * This module only computes "which Mahadasha is running now, and when did
 * it start/will it end" — Antardasha/Pratyantardasha subdivision (also
 * shown by the reference calculator) is intentionally out of scope for this
 * pass; see src/lib/calculator/service.ts's ChartData shape, which
 * currently only surfaces the Mahadasha level (`currentDasha`,
 * `dashaLordSanskrit`) to the UI.
 */

export type DashaLord = {
  /** Sanskrit name, matching src/lib/nakshatras.ts's VIMSHOTTARI_DASHAS spelling. */
  sanskrit: string;
  /** German display name, matching src/lib/sidereal.ts's DASHA_LORDS spelling. */
  german: string;
  years: number;
};

/** Fixed Vimshottari order + period lengths (total = 120 years), starting from Ketu. */
export const VIMSHOTTARI_ORDER: DashaLord[] = [
  { sanskrit: "Ketu", german: "Ketu", years: 7 },
  { sanskrit: "Shukra", german: "Venus", years: 20 },
  { sanskrit: "Surya", german: "Sonne", years: 6 },
  { sanskrit: "Chandra", german: "Mond", years: 10 },
  { sanskrit: "Mangala", german: "Mars", years: 7 },
  { sanskrit: "Rahu", german: "Rahu", years: 18 },
  { sanskrit: "Guru", german: "Jupiter", years: 16 },
  { sanskrit: "Shani", german: "Saturn", years: 19 },
  { sanskrit: "Budha", german: "Merkur", years: 17 },
];

const TOTAL_CYCLE_YEARS = VIMSHOTTARI_ORDER.reduce((sum, l) => sum + l.years, 0); // 120

export type CurrentDasha = {
  lord: DashaLord;
  /** Calendar start date of this Mahadasha. */
  start: Date;
  /** Calendar end date of this Mahadasha. */
  end: Date;
};

/**
 * @param moonSiderealLongitudeDeg Moon's sidereal ecliptic longitude at birth (0-360°).
 * @param birthDate The birth date/time (UTC instant is fine — Dasha timing
 *   below is on the scale of years, so timezone-of-day precision doesn't
 *   matter for which Mahadasha is "current" relative to birth).
 * @param now The date to evaluate "current" against (defaults to birth
 *   date itself, i.e. "which Dasha was running at the moment of birth" —
 *   callers wanting "which Dasha is running today" should pass `new Date()`).
 */
export function computeCurrentMahadasha(
  moonSiderealLongitudeDeg: number,
  birthDate: Date,
  now: Date = birthDate
): CurrentDasha {
  const NAKSHATRA_SPAN_DEG = 360 / 27; // 13°20'
  const normalized = ((moonSiderealLongitudeDeg % 360) + 360) % 360;
  const nakshatraIndex = Math.floor(normalized / NAKSHATRA_SPAN_DEG); // 0-26
  const fractionElapsedInNakshatra = (normalized % NAKSHATRA_SPAN_DEG) / NAKSHATRA_SPAN_DEG;

  // Each of the 27 Nakshatras is ruled by one of the 9 Vimshottari lords,
  // repeating the fixed order 3 times (27 / 9 = 3) — this is the classical
  // Nakshatra-to-Dasha-lord mapping (Ashwini→Ketu, Bharani→Venus, ... ,
  // Revati→Mercury, then repeating for the next 9, and the next 9).
  const startingLordIndex = nakshatraIndex % 9;

  // "Balance of dasha" at birth: the fraction of the starting lord's full
  // period that *remains* (1 - elapsed), since the elapsed portion belongs
  // to time before birth and isn't part of this person's own dasha
  // timeline going forward from the true start-of-Mahadasha date.
  const startingLord = VIMSHOTTARI_ORDER[startingLordIndex];
  const balanceYears = startingLord.years * (1 - fractionElapsedInNakshatra);
  const mahadashaStartOffsetYears = -(startingLord.years - balanceYears); // negative = before birth

  let cursor = addYears(birthDate, mahadashaStartOffsetYears);
  let lordIndex = startingLordIndex;

  // Walk forward through the fixed 120-year cycle until we find the
  // Mahadasha whose [start, end) window contains `now`. Bounded to a few
  // full cycles so this can never loop indefinitely on bad input.
  for (let i = 0; i < VIMSHOTTARI_ORDER.length * 3; i++) {
    const lord = VIMSHOTTARI_ORDER[lordIndex % VIMSHOTTARI_ORDER.length];
    const start = cursor;
    const end = addYears(cursor, lord.years);
    if (now.getTime() < end.getTime()) {
      return { lord, start, end };
    }
    cursor = end;
    lordIndex++;
  }

  // Unreachable in practice (3 full 120-year cycles = 360 years of margin
  // around any realistic birth-to-now gap), but keep TypeScript happy and
  // fail safely by returning the starting lord's window rather than
  // throwing.
  const lord = VIMSHOTTARI_ORDER[startingLordIndex];
  return { lord, start: birthDate, end: addYears(birthDate, lord.years) };
}

function addYears(date: Date, years: number): Date {
  const msPerYear = 365.2425 * 24 * 60 * 60 * 1000; // Julian year, consistent with TOTAL_CYCLE_YEARS's 120-year classical cycle length
  return new Date(date.getTime() + years * msPerYear);
}

// Re-exported so callers doing sanity checks against the "120-year cycle"
// claim don't need to re-sum VIMSHOTTARI_ORDER themselves.
export { TOTAL_CYCLE_YEARS };
